import { Loader } from '@googlemaps/js-api-loader';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { environment } from 'src/environments/environment';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { MbscOptionsService } from '@mobiscroll/angular';
import { polyfill } from 'mobile-drag-drop';
// optional import of scroll behaviour
import { scrollBehaviourDragImageTranslateOverride } from 'mobile-drag-drop/scroll-behaviour';
import { App as CapacitorApp } from '@capacitor/app';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { OfflineBaseComponent } from './core/public/offline-base.component';
import { Subscription } from 'rxjs';
import { ajaxCall } from './core/services/ajax-call.service';
import { JobOverlayDisplayService } from './services/job-overlay-display.service';
import { DeepLinkService } from './core/services/deep-link.service';

import * as LiveUpdates from '@capacitor/live-updates';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styles: [
    `
      .notifier__notification {
        z-index: 100000 !important;
      }
      @media (max-width: 575.98px) {
        notifier-notification {
          width: 95%;
        }
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent extends OfflineBaseComponent implements OnInit {
  title = 'nvms-portal-app';
  farSpinner = faSpinner;
  updateMessage: string | null = null; // for displaying update messages
  private lastLiveUpdateTime: number | null = null;

  constructor(
    private router: Router,
    protected mbscOptions: MbscOptionsService,
    private modal: NgbModal,
    private offCanvas: NgbOffcanvas,
    private ajax: ajaxCall,
    private jobOverlayService: JobOverlayDisplayService,
    private deepLinkService: DeepLinkService
  ) {
    super();

    const platform = Capacitor.getPlatform();
    if (platform === 'android') {
      document.body.classList.add('platform-android');
    } else if (platform === 'ios') {
      document.body.classList.add('platform-ios');
    }

    this.mbscOptions.setOptions({
      theme: 'ios',
      themeVariant: 'light',
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
      }

      if (event instanceof NavigationEnd) {
        //console.log(event.url);
      }

      if (event instanceof NavigationError) {
        // Present error to user
        //console.log(event.error);
      }
    });

    polyfill({
      // use this to make use of the scroll behaviour
      dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride,
    });

    // workaround to make scroll prevent work in iOS Safari >= 10
    try {
      window.addEventListener('touchmove', function () {}, { passive: false });
    } catch (e) {}
  }

  private loadedGoogleMaps: boolean = false;
  private offlineCheck: Subscription;

  async ngOnInit(): Promise<void> {
    this.offlineCheck = this.ajax.offlineWatch().subscribe({
      next: (status) => {
        if (status && !this.loadedGoogleMaps) {
          const loader = new Loader({
            apiKey: environment.googleMapEmbedApiKey,
            version: 'weekly',
          });

          loader.importLibrary('places').then(
            () => {
              this.loadedGoogleMaps = true;
            },
            () => {}
          );
        }
      },
    });

    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      //This listener works for devices only, not web.
      // alert(this.router.url);
      const goHomeRoutes = [
        '/public/jobs',
        '/public/jobs/',
        '/public/routes',
        '/public/schedule',
        '/public/home/notifications',
        '/public/settings',
      ];
      if (this.modal.hasOpenModals()) {
        this.modal.dismissAll();
      } else if (this.offCanvas.hasOpenOffcanvas()) {
        this.offCanvas.dismiss();
      } else if (this.jobOverlayService.isOpen()) {
        this.jobOverlayService.closeOverlay();
      } else if (goHomeRoutes.includes(this.router.url)) {
        this.router.navigateByUrl('/public/home');
      } else if (this.router.url == '/public/home') {
        CapacitorApp.exitApp();
      } else {
        return; // don't allow the back button to do anything, because sometimes back takes the user to a forward screen.
        //window.history.back();
      }
    });

    // Ensure the camera notch is not covered by the webview.
    if (Capacitor.isNativePlatform()) {
      StatusBar.setOverlaysWebView({ overlay: false }).catch((err) =>
        console.warn('StatusBar error:', err)
      );
    }

    // --------------------------------------------------------------------
    // Live Update
    // --------------------------------------------------------------------
    await this.checkForLiveUpdate();

    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.checkDailyLiveUpdate();
      }
    });
  }

  /**
   * Checks for a new Live Update, shows a message if updating,
   * and prevents the rest of the app from running until complete.
   */
  private async checkForLiveUpdate() {
    this.markLiveUpdateCheckedToday();
    try {
      this.updateMessage = `Checking for updates...`;
      console.log(
        `[LiveUpdates] Preparing to sync channel "${environment.liveUpdate.channel}"`
      );

      // 🔍 Get the current native plugin configuration
      const currentConfig = await LiveUpdates.getConfig();
      console.log('[LiveUpdates] Current native config:', currentConfig);

      // 🛠 Ensure the channel matches our environment at runtime
      if (currentConfig.channel !== environment.liveUpdate.channel) {
        console.log(
          `[LiveUpdates] Adjusting channel from "${currentConfig.channel}" to "${environment.liveUpdate.channel}"`
        );
        await LiveUpdates.setConfig({
          ...currentConfig,
          channel: environment.liveUpdate.channel,
        });
      }

      // 🚀 Run the sync
      console.log(
        `[LiveUpdates] Syncing with channel "${environment.liveUpdate.channel}"`
      );
      const result = await LiveUpdates.sync();
      console.log(
        '[LiveUpdates] Sync result:',
        JSON.stringify(result, null, 2)
      );

      if (result.snapshot && result.activeApplicationPathChanged) {
        this.updateMessage = `Installing update...`;

        // brief pause so the message displays
        await new Promise((res) => setTimeout(res, 800));

        console.log('[LiveUpdates] Update applied. Reloading app...');
        await LiveUpdates.reload(); // automatically restarts into the new version
      } else {
        console.log('[LiveUpdates] No new update or already on latest.');
        this.updateMessage = null; // continue app
      }
    } catch (error) {
      console.log('[LiveUpdates] Sync error:', error);
      this.updateMessage = `No network connection. Running offline version.`;

      // brief delay to show message before continuing
      setTimeout(() => {
        this.updateMessage = null;
      }, 2000);
    }
    this.markLiveUpdateCheckedToday();
  }

  //When the app becomes active, if it's been more than 12 hours since the last check, do another live update check.
  //This will mostly happen first thing in the morning when users open the app.
  private checkDailyLiveUpdate(): void {
    const now = Date.now();
    const twelveHours = 12 * 60 * 60 * 1000;
    //const twelveHours = 60 * 1000; //1 minute for testing
    if (
      this.lastLiveUpdateTime &&
      now - this.lastLiveUpdateTime < twelveHours
    ) {
      return;
    }
    this.checkForLiveUpdate();
  }

  private markLiveUpdateCheckedToday(): void {
    this.lastLiveUpdateTime = Date.now();
  }
}
